import { uid } from 'quasar';
import { defineStore } from 'pinia';
import stringUtil from 'src/utils/string';
import type { dynamicObj } from 'src/pages/assets';
import { ApiExceptions } from 'src/exceptions/index';
import { date_pattern_setting } from 'src/constants';
import displayFieldLabel from 'src/utils/displayFieldLabel';
import { isDateField } from 'src/utils/dateCalculatedField';
import formBuilderHelper from 'src/formBuilder/utils/formBuilderHelper';
import { FormBuilder } from 'src/formBuilder/form_elements/formbuilder.js';
import { formBuilderApiService, documentTypeApiService } from 'src/services';
import {
  formBuilderInputTypes,
  allowAbleModels,
} from 'src/formBuilder/formBuilderConstants';
import { i18n } from 'src/boot/i18n';
import { notify } from 'src/components/notifications/notify';
import {
  useDocumentStore,
  useAmountFormatStore,
  useSettingStore,
  useFormBuilderSettingsStore,
  useDocAttributesStore,
} from 'src/store/main';

export const useFormBuilderStore = defineStore('form_builder_store', {
  state: () => ({
    show_help: false,
    keyToUpdateComponnets: 0,
    isFormBuilderMode: false,
    formBuilderModel: [] as dynamicObj, // this mode will contain value relaetd properties like foemated value, value, coridinates array
    formBuilderConfigModel: [] as dynamicObj, //this mode will only contains the congigueations like width, lable,
    formLayoutImages: [] as any,
    proximityTokens: [] as any,
    activeFieldName: '',
    defaultFields: [] as any,
    defaultDocTypesGroups: [] as dynamicObj,
    defaultDocTypesFields: [] as dynamicObj,
    defaultGroups: [] as any,
    activeField: {} as dynamicObj,
    activeTab: 'form_elements',
    formLayout: [] as any,
    layout: [] as any,
    document_type_templates: [] as any,
    documentTables: [] as dynamicObj,
    loading_templates: false,
    renderComponent: true,
    isExtenedPanel: true,
    showTabIndex: false,
    showImages: false,
    docType: '',
    allowAreaSelection: false,
    markedAreasList: [] as any,
    documentTableNames: [] as any,
    fieldColors: new Map(),
    fieldListToHighlight: [] as any,
    coloredFields: [{} as any],
    amount_format_store: useAmountFormatStore(),
    date_format_pattern: date_pattern_setting,
    isDefaultFieldLoaded: false,
    isSaving: false,
    isAutoGenerating: false,
    isRestoringDefaults: false,
    freeze_fields_change_event: false,
    second_approval: false,
    reRenderGroupForm: 0,
    layout_template_id: '',
    isFieldUnderEditing: false,
    groupNamesPostFix: '__group',
  }),
  actions: {
    // isAutoTriggerField(field_name: string): boolean {
    //   const doc_ms_data_lookup_store = useDocMasterDataLookUpStore();
    //   const isAutoTrigger: boolean = doc_ms_data_lookup_store.fields_with_autotrigger.some((field: any) => field.field_name === field_name);
    //   return isAutoTrigger;
    // },
    // old Implementation
    // getFieldsToChange(field_name: string) {
    //   const doc_ms_data_lookup_store = useDocMasterDataLookUpStore();
    //   const auto_trigger_field = doc_ms_data_lookup_store.fields_with_autotrigger.find((field: any) => field.field_name === field_name);
    //   const highlight_fields: any = auto_trigger_field.group.fields
    //   this.highlightFields(highlight_fields);
    // },
    getFieldsToChange(field_name: string) {
      const doc_store = useDocumentStore();
      const autoTriggerObject = doc_store.lookup_configurations.get(field_name);
      this.highlightFields(autoTriggerObject.fields);
    },

    highlightFields(fields: any) {
      fields.forEach((field: any) => {
        this.colorField(field.field_name, false);
      });
    },

    colorField(field_name: string, colored = true) {
      field_name = this.getFieldName(field_name);
      const field_element = document.getElementById(field_name);
      if (field_element) {
        const classList = field_element.classList;
        let color = 'primary';
        if (colored) {
          color = this.fieldColors.get(field_name);
        }
        if (!color) {
          return;
        }
        classList.add('field-' + color);

        this.coloredFields.push({
          field_name: field_name,
          class: 'field-' + color,
        });
      }
    },

    getFieldName(field: string) {
      return field.replace('[', '').replace(']', '');
    },
    removeColorFromFields() {
      for (
        let index = 0;
        index < this.coloredFields.length;
        index = index + 1
      ) {
        const field_element = document.getElementById(
          this.coloredFields[index].field_name,
        );
        if (field_element) {
          const classList = field_element.classList;
          classList.remove(this.coloredFields[index].class);
        }
      }
    },
    showInHelpSection() {
      this.show_help = true;
    },

    getArtikelNummerFields() {
      const doc_store = useDocumentStore();

      if (doc_store.record.hasOwnProperty('artikelnummer_fields')) {
        return doc_store.record.artikelnummer_fields as string[];
      }

      return [];
    },

    getDocDateFormat() {
      const settings_store = useSettingStore();
      const val = settings_store.user_settings[this.date_format_pattern];
      return val ? String(val) : '';
    },

    getDocLocale() {
      const doc_store = useDocumentStore();
      const val = doc_store.record?.doc_locale;
      return val ? String(val) : '';
    },

    async loadDocumentDefaultFields(doc_type: string) {
      if (this.defaultDocTypesFields.hasOwnProperty(doc_type)) {
        this.defaultGroups = this.defaultDocTypesGroups[doc_type];
        this.prepareFields(this.defaultDocTypesFields[doc_type]);
        return;
      }

      const response =
        await formBuilderApiService.loadDocumentDefaultFields(doc_type);
      if (response.field_setting.length == 0) {
        this.defaultGroups = [];
        return;
      }

      Array.from(response.field_setting).forEach((group: any) => {
        Array.from(group.rows).forEach((rows: any) => {
          Array.from(rows).forEach((row: any) => {
            row.label = displayFieldLabel.methods.getLabelForFormBuilder(
              row,
              '',
            );
            row.displayLabelOnLeft = false;

            if (row.field_type.toLowerCase() === 'enum') {
              row.fieldType = formBuilderInputTypes.ENUM_INPUT;
              row.formatted_value = '';
              row.icon = 'mdi:format-list-checks';
            } else if (row.field_type.toLowerCase() === 'lov') {
              row.fieldType = formBuilderInputTypes.LOV_INPUT;
              row.formatted_value = '';
              row.icon = 'mdi:format-list-checks';
            } else if (row.field_type.toLowerCase() === 'boolean') {
              row.fieldType = formBuilderInputTypes.CHECK_BOX;
              row.formatted_value = false;
              row.icon = 'mdi:checkbox-marked';
            } else if (
              row.input_type &&
              row.input_type.toLowerCase() === 'user'
            ) {
              row.fieldType = formBuilderInputTypes.USER;
              row.formatted_value = '';
              row.icon = 'mdi:account-multiple';
            } else if (
              row.input_type &&
              row.input_type.toLowerCase() === 'group'
            ) {
              row.fieldType = formBuilderInputTypes.GROUP_DROPDOWN;
              row.formatted_value = '';
              row.icon = 'mdi:group';
            } else {
              row.fieldType = formBuilderInputTypes.TEXT_INPUT;
              row.formatted_value = '';
              row.icon = 'mdi:format-text';
              if (row.fuzzy_name && row.fuzzy_name != null) {
                row.icon = 'mdi:text-search';
              }
            }
            row.name = row.field_name;
            row.labelWidth = 20;
            row.elementWidth = 33;
            row.isDefaultField = true;
            // Preserve is_hidden from API, map to isHidden for frontend consistency
            row.is_hidden = row.is_hidden ?? false;
            row.isSearched = true;
            row.is_textarea = false;
            row.isAlreadyAdded = false;
            this.defaultFields.push(row);
          });
        });
      });

      this.defaultGroups = response.field_setting;
      this.defaultDocTypesGroups[doc_type] = this.defaultGroups;
      this.defaultDocTypesFields[doc_type] = response.field_setting;
    },
    prepareFields(groups: any) {
      this.defaultFields = [];
      Array.from(groups).forEach((group: any) => {
        Array.from(group.rows).forEach((rows: any) => {
          Array.from(rows).forEach((row: any) => {
            //Re-setting props to make sure everything is reset on a new layout.
            row.labelWidth = 20;
            row.elementWidth = 33;
            row.isDefaultField = true;
            // Preserve is_hidden from API, map to isHidden for frontend consistency
            row.is_hidden = row.is_hidden ?? false;
            row.isSearched = true;
            row.is_textarea = false;
            row.isAlreadyAdded = false;
            this.defaultFields.push(row);
          });
        });
      });
    },

    async getTemplatesForDocuments() {
      this.document_type_templates = [];

      try {
        this.loading_templates = true;
        const response = await formBuilderApiService.getAllLayoutTemplates({});
        if (response.success === false) {
          throw response;
        }

        this.document_type_templates = [...response.data];
      } catch (err) {
        const msg = ApiExceptions.getErrorMessage(err as dynamicObj);
        logError(msg);
        logError(err);
      } finally {
        this.loading_templates = false;
      }
    },
    async getLayoutTemplate(prams: any, throwError = true) {
      try {

        // CRITICAL: Require doc_type before querying layout templates
        // Without this guard, an unfiltered query returns ALL templates sorted alphabetically,
        // causing the wrong template to be selected.
        if (!prams.doc_type) {
          console.warn(
            '[getLayoutTemplate] Skipping - doc_type not yet loaded. This prevents fetching wrong template.',
          );
          return;
        }
        const doc_attribute_store = useDocAttributesStore();
        const query_data = {} as dynamicObj;
        if (prams.doc_type) {
          query_data['doc_type_key'] = prams.doc_type;
        }

        if (prams.sub_doc_type) {
          query_data['sub_doc_type_key'] = prams.sub_doc_type;
        }

        if (prams.doc_origin) {
          query_data['doc_origin'] = prams.doc_origin;
        }

        if (prams.layout_type) {
          query_data['layout_type'] = prams.layout_type;
        }

        if (prams.include_layout_json) {
          query_data['include_layout_json'] = prams.include_layout_json;
        }

        const response = await formBuilderApiService.getLayoutTemplates(query_data);
        if ( !response || (response.hasOwnProperty('success') && response.success === false) ) {
          throw response;
        }

        if (
          !response.data ||
          !Array.isArray(response.data) ||
          response.data.length === 0 ||
          !response.data[0].hasOwnProperty('id')
        ) {
          const docType = prams.doc_type;
          const layoutType = prams.layout_type || 'approval';

          const path = `/layout-builder/${docType}/${layoutType}`;

          const fullUrl = `${window.location.origin}${path}`;
          const rawMessage = i18n.t('layout_template_id_not_found');
          const finalMessage = rawMessage.replace('{{url}}', fullUrl);

          throw { message: finalMessage };
        }

        const data = response.data[0];

        this.layout_template_id = data.id;

        if (data.layout_json) {
          const layout_json = JSON.parse(data.layout_json);
          if (prams.is_auto_accounting) {
            this.layout = layout_json.hasOwnProperty('layout') ? layout_json.layout : [];
          } else {
            this.formLayout = layout_json.hasOwnProperty('layout') ? layout_json.layout : [];
          }

          this.isExtenedPanel = layout_json.isExtenedPanelEnabled ? true : false;

          const form_builder_settings_store = useFormBuilderSettingsStore();
          form_builder_settings_store.setGeneralSettings(layout_json);
        }
      } catch (err: any) {
        if (throwError) {
          const msg = ApiExceptions.getErrorMessage(err as dynamicObj);
          notify.error(msg);
          logError(err);
        }
      }
    },

    updateConfigModel(newField: any) {
      this.formBuilderConfigModel[newField.name] = JSON.parse(
        JSON.stringify(newField),
      );
      if (
        !this.formBuilderConfigModel[newField.name].hasOwnProperty('tabIndex')
      ) {
        this.formBuilderConfigModel[newField.name].tabIndex = 0;
      }
    },

    getFieldDef(FieldType: string) {
      for (const field of FormBuilder.data().fields) {
        if (field.fieldType === FieldType) {
          return field;
        }
      }
    },

    getChildElements(fieldDef: any) {
      const baseFieldDef = this.getFieldDef(fieldDef.fieldType);
      if (!baseFieldDef || !this.hasChildElements(baseFieldDef)) {
        return [] as dynamicObj[];
      }

      const base_child_elements = baseFieldDef.child_elements || [];
      if (base_child_elements.length === 0) {
        return [] as dynamicObj[];
      }

      const new_elements = [] as dynamicObj[];
      base_child_elements.forEach((def: any) => {
        let field = def as dynamicObj;
        const element = fieldDef.child_elements?.find(
          (item: dynamicObj) => item.fieldType === field.fieldType,
        );
        if (element) {
          field = element;
        }
        new_elements.push(field);
      });

      return new_elements;
    },

    hasChildElements(fieldDef: any) {
      if (
        !fieldDef ||
        !fieldDef.hasOwnProperty('child_elements') ||
        !Array.isArray(fieldDef.child_elements)
      ) {
        return false;
      }
      return true;
    },

    renderChildElements(field_name: string) {
      try {
        const newField = this.formBuilderConfigModel[field_name];
        if (!this.hasChildElements(newField)) {
          return;
        }

        const child_elements = this.getChildElements(newField);
        this.formBuilderConfigModel[field_name].child_elements = child_elements;
        child_elements.forEach((field: dynamicObj, index: number) => {
          const fieldDef = this.getFieldDef(field.fieldType);
          if (!fieldDef) {
            return;
          }

          this.updateConfigModel(fieldDef);

          // keep this simple
          const final_item = Object.assign(
            {},
            {
              name: fieldDef.name,
              fieldType: fieldDef.fieldType,
            },
          );
          this.formBuilderConfigModel[field_name].child_elements[index] =
            final_item;
        });
      } catch (error) {
        logError(error);
      }
    },

    prepareLayoutData() {
      try {
        for (const group of this.formLayout) {
          for (const row of group.rows) {
            let tempObj = {} as dynamicObj;
            if (this.formBuilderConfigModel[row.name]) {
              tempObj = JSON.parse(
                JSON.stringify(this.formBuilderConfigModel[row.name]),
              );
            }

            this.formBuilderConfigModel[row.name] = Object.assign(
              {},
              row,
              tempObj,
            );

            // Merge isHidden from defaultFields (Field Settings) - AFTER Object.assign so it won't be overwritten
            const defaultField = this.defaultFields.find(
              (f: any) => f.field_name === row.field_name || f.name === row.name
            );
            if (defaultField && defaultField.hasOwnProperty('isHidden')) {
              this.formBuilderConfigModel[row.name].isHidden = defaultField.isHidden;
            }
            if (this.hasChildElements(this.formBuilderConfigModel[row.name])) {
              this.renderChildElements(row.name);
            }

            if (!allowAbleModels.includes(row.fieldType)) {
              continue;
            }

            if (row.fieldType === formBuilderInputTypes.TABLE_OF_CHECKBOXES) {
              row.tableBodyRows.forEach(
                (tableBodyRow: any, bodyRowsIndex: any) => {
                  tableBodyRow.forEach(
                    (tableBodyColumn: any, bodyColumnIndex: any) => {
                      if (
                        !this.formBuilderModel.hasOwnProperty(
                          tableBodyColumn.name,
                        )
                      ) {
                        const tempObj = {
                          confidence: 0,
                          name: tableBodyColumn.name,
                          value: false,
                          formatted_value: false,
                          content: false,
                        };

                        this.formBuilderModel[tableBodyColumn.name] = tempObj;
                      }
                      this.formBuilderConfigModel[tableBodyColumn.name] =
                        JSON.parse(JSON.stringify(tableBodyColumn));
                    },
                  );
                },
              );
              continue;
            }

            if (!this.formBuilderModel.hasOwnProperty(row.name)) {
              this.formBuilderModel[row.name] = [];
              this.formBuilderModel[row.name].page = '';
              this.formBuilderModel[row.name].color = '';
              this.formBuilderModel[row.name].score = '';
              this.formBuilderModel[row.name].coords = [];
              this.formBuilderModel[row.name].confidence = '';

              if (row.fieldType === formBuilderInputTypes.MULTI_CHECK_BOX) {
                this.formBuilderModel[row.name].groupList = [];
                this.formBuilderModel[row.name].value = '';
                this.formBuilderModel[row.name].formatted_value = '';
              } else if (row.fieldType === formBuilderInputTypes.CHECK_BOX) {
                this.formBuilderModel[row.name].value = false;
                this.formBuilderModel[row.name].formatted_value = false;
              } else {
                this.formBuilderModel[row.name].value = '';
                this.formBuilderModel[row.name].formatted_value = '';
              }
            }
          }
        }
      } catch (error) {
        logError(error);
      }
    },

    async getDocumentTables(doc_type: string) {
      if (Object.keys(this.documentTables).length > 0) {
        return;
      }

      try {
        const response = await documentTypeApiService.getTableColumns(doc_type);

        if (
          response.data &&
          response.data.hasOwnProperty('success') &&
          response.success === false
        ) {
          throw { message: response.data.message };
        }

        this.documentTables = response.data;
      } catch (err) {
        const msg = ApiExceptions.getErrorMessage(err as dynamicObj);
        logError(err, msg);
      }
    },

    getElementWidth(element: any, isGroup = false) {
      try {
        let elementName = element.name;
        if (isGroup) {
          elementName = this.getGroupKey(element.name);
        }

        if (this.formBuilderConfigModel.hasOwnProperty(elementName)) {
          return this.formBuilderConfigModel[elementName]
            .elementWidth as string;
        }

        return element.elementWidth as string;
      } catch (error) {
        logError(
          'getElementWidth:: Error ',
          element.name,
          this.formBuilderConfigModel[element.name],
        );
      }
    },
    replaceElementInLayout(newFieldName: string, oldFieldName: string) {
      exit_loops: for (const group of this.formLayout) {
        for (const row of group.rows) {
          if (row.name === oldFieldName) {
            row.name = newFieldName;

            break exit_loops;
          }
        }
      }

      this.formBuilderConfigModel[newFieldName] = [];
      this.formBuilderConfigModel[newFieldName] =
        this.formBuilderConfigModel[oldFieldName];

      this.formBuilderModel[newFieldName] = [];
      this.formBuilderModel[newFieldName] = this.formBuilderModel[oldFieldName];

      delete this.formBuilderModel[oldFieldName];
      delete this.formBuilderConfigModel[oldFieldName];

      this.activeFieldName = newFieldName;
    },

    // async performCalulations( fieldName: string, formula: string, amount_format_locale: string, field_type?: string ) {
    //   if (field_type && isDateField(field_type) || fieldName == 'due_date') {
    //     return;
    //   }

    //   let tempFormulaString = '';
    //   let tempStrContainer = '';
    //   const tempArray = formula.split(' ');

    //   for (let index = 0; index < tempArray.length; index = index + 1) {
    //     tempStrContainer = tempArray[index].trim();
    //     if (tempArray[index].startsWith('[')) {
    //       tempStrContainer = tempStrContainer.replace('[', '');
    //       tempStrContainer = tempStrContainer.replace(']', '');

    //       if ( this.formBuilderModel[tempStrContainer] && this.formBuilderModel[tempStrContainer].value) {

    //         tempStrContainer = this.formBuilderModel[tempStrContainer].value;
    //         tempFormulaString = tempFormulaString + parseFloat(tempStrContainer).toString();
    //       } else {
    //         tempFormulaString = tempFormulaString + '0';
    //       }

    //     } else {
    //       tempFormulaString = tempFormulaString + tempStrContainer;
    //     }
    //   }

    //   this.formBuilderModel[fieldName].value = eval(tempFormulaString).toFixed(2);
    //   this.formBuilderModel[fieldName].formatted_value = await this.amount_format_store.formatValue(this.formBuilderModel[fieldName].value, 'en_US', amount_format_locale );
    // },

    async performCalulations(
      fieldName: string,
      formula: string,
      amount_format_locale: string,
      field_type?: string,
    ) {
      if ((field_type && isDateField(field_type)) || fieldName == 'due_date') {
        return;
      }

      let tempFormulaString = '';
      let tempStrContainer = '';
      const tempArray = formula.split(' ');
      const values = [];

      for (let index = 0; index < tempArray.length; index = index + 1) {
        tempStrContainer = tempArray[index].trim();
        if (tempArray[index].startsWith('[')) {
          tempStrContainer = tempStrContainer.replace('[', '');
          tempStrContainer = tempStrContainer.replace(']', '');

          if (
            this.formBuilderModel[tempStrContainer] &&
            this.formBuilderModel[tempStrContainer].formatted_value &&
            this.formBuilderModel[tempStrContainer].formatted_value != null &&
            this.formBuilderModel[tempStrContainer].formatted_value != ''
          ) {
            values.push({
              key: this.formBuilderModel[tempStrContainer].formatted_value,
              value: this.formBuilderModel[tempStrContainer].formatted_value,
            });
          }
        }
      }

      const formatted_values = await this.amount_format_store.formatMultiValues(
        values,
        amount_format_locale,
      );

      for (let index = 0; index < tempArray.length; index = index + 1) {
        tempStrContainer = tempArray[index].trim();
        if (tempArray[index].startsWith('[')) {
          tempStrContainer = tempStrContainer.replace('[', '');
          tempStrContainer = tempStrContainer.replace(']', '');

          if (
            this.formBuilderModel[tempStrContainer] &&
            this.formBuilderModel[tempStrContainer].formatted_value &&
            this.formBuilderModel[tempStrContainer].formatted_value != null &&
            this.formBuilderModel[tempStrContainer].formatted_value != ''
          ) {
            const amount_formatted = this.amount_format_store.searchValueByKey(
              this.formBuilderModel[tempStrContainer].formatted_value,
              formatted_values,
            );
            tempStrContainer = amount_formatted;
            tempFormulaString =
              tempFormulaString + parseFloat(tempStrContainer).toString();
          } else {
            tempFormulaString = tempFormulaString + '0';
          }
        } else {
          tempFormulaString = tempFormulaString + tempStrContainer;
        }
      }

      this.formBuilderModel[fieldName].value =
        eval(tempFormulaString).toFixed(2);
      this.formBuilderModel[fieldName].formatted_value =
        await this.amount_format_store.formatValue(
          this.formBuilderModel[fieldName].value,
          'en_US',
          amount_format_locale,
        );
    },

    isFieldHidden(field: any) {
      try {
        if (this.formBuilderConfigModel[field.name]) {
          if ( this.formBuilderConfigModel[field.name].hasOwnProperty('is_hidden') && this.formBuilderConfigModel[field.name].is_hidden === true) {
            return true;
          }
          return false;
        }

        if (field.hasOwnProperty('is_hidden') && field.is_hidden === true) {
          return true;
        }
      } catch (err) {
        logError(err);
      }
      return false;
    },

    hasExplanation(fieldName: string) {
      const explanationKey = `${fieldName}_explanation`;
      const translation = i18n.t(explanationKey);
      return translation !== explanationKey;
    },

    isExtraInfo(field: any): any {
      if (!field || this.isFormBuilderMode) {
        return false;
      }

      if (this.hasExplanation(field)) {
        return true;
      }

      if (!this.isDocumentExtratedField(field)) {
        return true;
      }

      if (this.hasInputError(field)) {
        return true;
      }

      return formBuilderHelper.methods.getLowerConfidence(field);
    },

    isDocumentExtratedField(fieldName: string) {
      const field_value = this.formBuilderModel[fieldName].formatted_value;
      const coords = this.formBuilderModel[fieldName].coords;

      if (field_value && (!coords || coords.length == 0)) {
        return false;
      }

      return true;
    },
    hasInputError(fieldName: string) {
      if (!this.formBuilderModel[fieldName].input_error) {
        return false;
      }

      return true;
    },
    showClearRuleButton(fieldName: string) {
      if (!fieldName || this.isFormBuilderMode) {
        return false;
      }

      return !this.formBuilderModel[fieldName].deleted &&
        this.formBuilderModel[fieldName].rule
        ? true
        : false;
    },
    duplicateSubGroup(
      groupIndex: number,
      itemIndex: number,
      itemName = '',
      validateLastGroupData = false,
    ) {
      const subGroup = JSON.parse(
        JSON.stringify(this.formLayout[groupIndex].rows[itemIndex]),
      );

      if (subGroup.rows.length == 0) {
        return;
      }

      subGroup.isParent = false;

      const { nextIndex, lastVisitedField } = this.findIndexToAddNewItem(
        itemIndex,
        subGroup.rows[0].name,
        groupIndex,
        subGroup.rows[0].name,
      );

      let index = stringUtil.methods.getStringLastPart(
        itemName ? itemName : lastVisitedField,
        '__',
      );

      if (validateLastGroupData) {
        const lastChildIndex = nextIndex - 1;
        const isValidationFailed = this.validateLastItem(
          groupIndex,
          lastChildIndex,
        );
        if (isValidationFailed) {
          return false;
        }
      }

      if (!itemName) {
        index = index + 1;
      }

      for (const element of subGroup.rows) {
        element.parent_field_name = element.name;
        element.name = element.name + '__' + index;
        element.field_name = element.name;
        element.coords = [];
        element.isDefaultField = false;

        const customEvent = new CustomEvent('prepare_model', {
          detail: { fieldDef: element, field_name: element.name },
        });
        document.dispatchEvent(customEvent);
      }

      subGroup.indexByType = nextIndex - itemIndex + 1;
      subGroup.subGroupCount = nextIndex - itemIndex + 1;

      this.formLayout[groupIndex].rows.splice(nextIndex, 0, subGroup);
      this.prepareIndexesForSubGroups(groupIndex);

      return true;
    },
    findIndexToAddNewItem(
      itemIndex: number,
      parentName: string,
      groupIndex: number,
      lastVisitedField: string,
    ): any {
      const nextIndex = itemIndex + 1;

      // means item is the last index
      if (this.formLayout[groupIndex].rows.length == nextIndex) {
        return { nextIndex, lastVisitedField };
      }

      // find the next index of current group
      const nextItem = this.formLayout[groupIndex].rows[nextIndex];

      if (nextItem.fieldType === formBuilderInputTypes.SUB_GROUP) {
        if (parentName != nextItem.rows[0].parent_field_name) {
          return { nextIndex, lastVisitedField };
        }
        return this.findIndexToAddNewItem(
          nextIndex,
          parentName,
          groupIndex,
          nextItem.rows[0].name,
        );
      }

      return { nextIndex, lastVisitedField };
    },

    validateLastItem(groupIndex: number, lastItemIndex: number) {
      const subGroup = this.formLayout[groupIndex].rows[lastItemIndex];

      const subGroupRows = subGroup.rows;

      let isRowEmpty = true;
      for (const row of subGroupRows) {
        if (this.formBuilderModel[row.field_name].formatted_value) {
          isRowEmpty = false;
        }
      }

      return isRowEmpty;
    },

    prepareIndexesForSubGroups(groupIndex: number) {
      let indexByType = 1;
      let subGroupCount = 1;

      for (const [index, item] of this.formLayout[groupIndex].rows.entries()) {
        if (item.fieldType != formBuilderInputTypes.SUB_GROUP) {
          continue;
        }
        item.indexByType = indexByType;
        item.subGroupCount = subGroupCount;

        const nextItem = this.formLayout[groupIndex].rows[index + 1];

        if (!nextItem || item.label != nextItem.label) {
          indexByType = 0;
        }

        indexByType++;
        subGroupCount++;
      }
    },

    isTabIndexField(fieldName: string) {
      if (
        formBuilderInputTypes.TEXT_INPUT ===
          this.formBuilderConfigModel[fieldName].fieldType ||
        formBuilderInputTypes.CHECK_BOX ===
          this.formBuilderConfigModel[fieldName].fieldType ||
        formBuilderInputTypes.MULTI_CHECK_BOX ===
          this.formBuilderConfigModel[fieldName].fieldType
      ) {
        return true;
      }
      return false;
    },

    handleTabIndexChange(fieldName: string) {
      // if setting is not enabled return
      const form_builder_settings_store = useFormBuilderSettingsStore();
      if (!form_builder_settings_store.autoGenerateTabIndex) {
        return;
      }
      let changeTabIndex = false;
      let newTabIndex = 0;
      let previousTabIndex = 0;

      for (const group of this.formLayout) {
        for (const row of group.rows) {
          if (!this.isTabIndexField(row.name)) {
            continue;
          }

          if (changeTabIndex) {
            newTabIndex++;
            this.formBuilderConfigModel[row.name].tabIndex = newTabIndex;
            row.tabIndex = newTabIndex;
            continue;
          }

          if (row.name === fieldName) {
            changeTabIndex = true;
            newTabIndex = this.formBuilderConfigModel[row.name].tabIndex;

            if (
              newTabIndex == null ||
              newTabIndex == undefined ||
              newTabIndex == 0
            ) {
              newTabIndex = previousTabIndex ? +previousTabIndex + 1 : 0;
              this.formBuilderConfigModel[row.name].tabIndex = newTabIndex;
              row.tabIndex = newTabIndex;
            }

            continue;
          }

          previousTabIndex = this.formBuilderConfigModel[row.name].tabIndex;
        }
      }
    },

    transformDataToV2(formLayout: any) {
      let saveRequired = false;

      const fieldList = [] as any;
      const groupsList = [] as any;

      for (const item of formLayout) {
        if (item.hasOwnProperty('rows')) {
          groupsList.push(item);
          continue;
        }

        fieldList.push(item);
      }

      // already data in v2 formate do nothing
      if (!fieldList || fieldList.length == 0) {
        this.formLayout = formLayout;
        return saveRequired;
      }

      // transform data in v2 formate
      const group = {
        fieldType: formBuilderInputTypes.GROUP,
        name: 'group_' + uid(),
        title: '',
        group_key: '',
        is_panel_section_group: false,
        elementWidth: 100,
        // 'height': 100,
        right_border: false,
        bottom_border: false,
        is_stcky_group: false,
        short_cut_key_1: '',
        short_cut_key_2: '',
        rows: JSON.parse(JSON.stringify(fieldList)),
      };

      saveRequired = true;
      this.formLayout = [];
      formLayout = [...groupsList];
      formLayout.push(group);

      this.formLayout = formLayout;
      return saveRequired;
    },
    getGroupKey(groupName: string) {
      return groupName + this.groupNamesPostFix;
    },
  },
});
